import { useState } from "react"

import { IconChevronDown } from "@tabler/icons-react"
import { Column } from "@tanstack/react-table"
import { P, match } from "ts-pattern"

import { cn } from "@/lib/utils"

import { DatePicker } from "../../DatePicker"
import { Divider } from "../../Divider"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../DropdownMenu"

enum Comparison {
  Before = "Before",
  After = "After",
  On = "On",
  Between = "Between",
  IsEmpty = "IsEmpty",
}

const ComparisonLabel = {
  [Comparison.Before]: "before",
  [Comparison.After]: "after",
  [Comparison.On]: "on",
  [Comparison.Between]: "between",
  [Comparison.IsEmpty]: "is empty",
}

function formatDate(date?: Date) {
  return date?.toISOString()?.slice(0, 10)
}

export function DateRangeFilter<T extends Record<string, any> = object>({
  col,
  filterValue,
}: {
  col: Column<T>
  filterValue?: [string, string, boolean]
}) {
  const [comparison, setComparison] = useState<Comparison>(() =>
    match(filterValue)
      .with([P._, P._, true], () => Comparison.IsEmpty)
      .with([P.nullish, P.nonNullable, false], () => Comparison.Before)
      .with([P.nonNullable, P.nullish, false], () => Comparison.After)
      .with([P.nonNullable, P.nonNullable, false], ([startDate, endDate, _]) =>
        startDate === endDate ? Comparison.On : Comparison.Between,
      )
      .otherwise(() => Comparison.Before),
  )

  const [startDate, setStartDate] = useState<Date | undefined>(() => {
    if (filterValue?.[0]) {
      return new Date(filterValue?.[0])
    }

    return undefined
  })

  const [endDate, setEndDate] = useState<Date | undefined>(() => {
    if (filterValue?.[1]) {
      return new Date(filterValue?.[1])
    }

    return undefined
  })

  const label = col.columnDef.meta?.textLabel ?? col.columnDef.header
  if (typeof label !== "string") {
    throw new Error(
      "column `header` must be a string, or `meta.textLabel` must be set.",
    )
  }
  const showInput = comparison !== Comparison.IsEmpty

  return (
    <>
      <DropdownMenuLabel>{label}</DropdownMenuLabel>
      <DropdownMenuSeparator />
      <form
        onSubmit={(event) => {
          event.preventDefault()
          col.setFilterValue([
            formatDate(startDate),
            formatDate(endDate),
            comparison === Comparison.IsEmpty,
          ])
        }}
      >
        <div
          className={cn(
            "-mx-1 grid grid-cols-[auto_auto_minmax(0,1fr)] gap-2 rounded px-1",
            {
              "grid-cols-[auto_auto_minmax(0,1fr)_auto_minmax(0,1fr)]":
                comparison === Comparison.Between,
              "grid-cols-[minmax(0,1fr)]": comparison === Comparison.IsEmpty,
            },
          )}
        >
          <DropdownMenu>
            <DropdownMenuTrigger
              className={cn(
                "relative flex cursor-default items-center justify-between gap-3 rounded-sm px-2 py-1.5 text-body1 text-colors-text-secondary outline-hidden transition-colors select-none hover:bg-colors-bg-hover focus:bg-colors-bg-hover data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
              )}
            >
              <span>{ComparisonLabel[comparison]}</span>
              <IconChevronDown size={16} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {Object.entries(ComparisonLabel).map(([key, value]) => (
                <DropdownMenuItem
                  key={key}
                  onSelect={() => {
                    setComparison(Comparison[key as keyof typeof Comparison])
                    setStartDate(undefined)
                    setEndDate(undefined)
                  }}
                >
                  {value}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {showInput && <Divider vertical />}
          {match(comparison)
            .with(Comparison.After, () => (
              <DatePicker
                mode="single"
                selected={startDate}
                onSelect={(date) => {
                  if (date) {
                    setStartDate(date)
                    setEndDate(undefined)
                  }
                }}
              />
            ))
            .with(Comparison.Before, () => (
              <DatePicker
                mode="single"
                selected={endDate}
                onSelect={(date) => {
                  if (date) {
                    setStartDate(undefined)
                    setEndDate(date)
                  }
                }}
              />
            ))
            .with(Comparison.On, () => (
              <DatePicker
                mode="single"
                selected={startDate}
                onSelect={(date) => {
                  if (date) {
                    setStartDate(date)
                    setEndDate(date)
                  }
                }}
              />
            ))
            .with(Comparison.Between, () => (
              <DatePicker
                mode="range"
                selected={{ from: startDate, to: endDate }}
                onSelect={(range) => {
                  if (range) {
                    setStartDate(range.from)
                    setEndDate(range.to)
                  }
                }}
              />
            ))
            .with(Comparison.IsEmpty, () => null)
            .exhaustive()}
        </div>
        <DropdownMenuSeparator />
        <button
          type="submit"
          className="relative flex w-full cursor-default items-center gap-3 rounded-sm px-2 py-1.5 text-body2 outline-hidden transition-colors select-none hover:bg-colors-bg-hover focus:bg-colors-bg-hover data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
          tabIndex={3}
        >
          Apply
        </button>
      </form>
    </>
  )
}
